import type { AvatarState, ConnectionState } from '../types';

export interface VoiceLiveDisplayStateInput {
  /** WebSocket session lifecycle state. */
  connectionState: ConnectionState;
  /** WebRTC avatar lifecycle state. */
  avatarState: AvatarState;
  /** Whether video is currently enabled. */
  isVideoEnabled: boolean;
  /** Whether the session has degraded to audio-only fallback. */
  isDegraded: boolean;
}

export interface VoiceLiveDisplayState {
  /**
   * Network-forced audio-only fallback that has reached a connected/ready
   * WebSocket session — used to swap the avatar overlay for the waveform.
   */
  audioFallbackConnected: boolean;
  /**
   * User deliberately chose audio-only (not a degradation) and the session is
   * fully ready. Kept distinct so the degraded banner is never shown.
   */
  voluntaryAudioConnected: boolean;
  /** Whether to show the audio waveform in place of the avatar video. */
  showAudioVisualizer: boolean;
  /** Whether to show the connection/start/error overlay. */
  showConnectionOverlay: boolean;
}

/**
 * Derive the avatar-vs-audio display flags from the current connection,
 * avatar, video, and degraded states. Pure: same inputs always yield the same
 * flags, so it is trivially testable in isolation from the component.
 */
export const selectVoiceLiveDisplayState = ({
  connectionState,
  avatarState,
  isVideoEnabled,
  isDegraded,
}: VoiceLiveDisplayStateInput): VoiceLiveDisplayState => {
  const audioFallbackConnected =
    isDegraded && (connectionState === 'connected' || connectionState === 'ready');

  const voluntaryAudioConnected = !isVideoEnabled && !isDegraded && connectionState === 'ready';

  const showAudioVisualizer =
    (!isVideoEnabled && avatarState === 'active') ||
    audioFallbackConnected ||
    voluntaryAudioConnected;

  const showConnectionOverlay =
    avatarState !== 'active' && !audioFallbackConnected && !voluntaryAudioConnected;

  return {
    audioFallbackConnected,
    voluntaryAudioConnected,
    showAudioVisualizer,
    showConnectionOverlay,
  };
};
